import React, {
    useState,
    useRef,
    useEffect
} from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash/get';

import BREAKPOINTS from '@mlb/react-patterns/style/breakpoints.ts';
import useClickOutside from '@mlb/react-patterns/components/hooks/useClickOutside';

import {
    useMedia,
    useSuggestions,
} from 'bdvp-common/src/hooks';
import {
    SUGGESTION_TYPES,
    SUGGESTION_FIELD_IDS,
} from 'bdvp-common/src/utils/typeaheadConstants';
import { useDictionary, useMediaConfig } from 'bdvp-common/src/hooks/config';
import { MEDIA_CONFIG_CONTEXT } from 'bdvp-common/src/utils/constants';
import { Tracking } from 'bdvp-tracking';

import { FILTER_BREAKPOINT } from '../../utils/constants';
import FilterSelection from '../../models/FilterSelection';
import { NoResultsMessage } from '../SuggestionMessages/';
import {
    Typeahead,
    Skeleton,
} from './QuickSearch.styles';

const baseImageProps = {
    width: '48px',
    height: '48px',
};

const QuickSearch = (props) => {

    const {
        cancelButtonRef,
        typeaheadInputRef,
        setFilters,
        closeFilterModal,
        onShowSuggestions,
        fieldsData,
        loading,
    } = props;

    const dictionary = useDictionary();
    const config = useMediaConfig(MEDIA_CONFIG_CONTEXT.search) ?? {};

    /** State */
    const [
        searchValue,
        setSearchValue,
    ] = useState('');

    /** Media Query */
    const isFilterInModal = useMedia(
        [`(min-width: ${BREAKPOINTS[FILTER_BREAKPOINT]})`],
        [false],
        true
    );

    /** Suggestions Logic */
    const {
        suggestions,
        clearSuggestions,
    } = useSuggestions({
        value: searchValue,
        noResultsPlaceholder: [
            {
                content: (
                    <NoResultsMessage dictionary={dictionary} />
                ),
            },
        ],
        displayCount: 10,
    });

    useEffect(() => {

        if(suggestions.length) {

            onShowSuggestions(true);

        } else {

            onShowSuggestions(false);

        }

    }, [
        onShowSuggestions,
        suggestions,
    ]);

    // If we are on desktop and if no parent component create a ref for the typeahead, we create one here;
    const localTypeaheadRef = useRef(null);
    // InputRef is the reference of the typeahead component
    const inputRef = typeaheadInputRef || localTypeaheadRef;
    const clickOutsideRef = isFilterInModal
        ? {}
        : inputRef;

    useClickOutside(clickOutsideRef, clearSuggestions);

    /** Handlers */
    const onChangeHandler = (event) => {

        const inputValue = event.target.value;
        const inputEmpty = inputValue === '';

        if (inputEmpty) {

            clearSuggestions();

        }

        setSearchValue(inputValue);

    };

    const onItemClickHandler = (event, info) => {

        const selection = _get(suggestions, `[${info.index}]`);
        let filterData;
        if(selection.type === SUGGESTION_TYPES.teams) {

            filterData = fieldsData.find(({ id }) => id === SUGGESTION_FIELD_IDS.teams);

        } else if (selection.type === SUGGESTION_TYPES.players) {

            filterData = fieldsData.find(({ id })=> id === SUGGESTION_FIELD_IDS.players);

        } else if (selection.type === SUGGESTION_TYPES.tags) {

            filterData = fieldsData.find(({ id })=> id === SUGGESTION_FIELD_IDS.tags);

        } else {

            return;

        }

        const filtersConfig = _get(config, 'filterGroups', []).reduce(
            (arr, elem) => [
                ...arr,
                ...elem.filters,
            ],
            []
        );
        const filterConfig = filtersConfig.find(({ fieldId }) => fieldId == filterData.id) || {};
        const selected = [
            {
                label: selection.name,
                value: filterData.type === 'NUMBER' ? Number(selection.value) : selection.value,
            },
        ];

        clearSuggestions();
        setSearchValue('');
        const filter = new FilterSelection({
            name: filterData.name,
            fieldId: filterData.id,
            displayName: filterConfig.fields?.[0].overrides?.displayName || filterData.displayName,
            comparator: filterData.defaultOperator,
        }, selected);

        setFilters([filter]);

        Tracking.trackQuickSearchSelect(filterData.displayName, selection.value);

        if(isFilterInModal) {

            closeFilterModal();

        }

    };

    const onFocusHandler = () => {

        Tracking.trackSearchInputClick();

    };

    const onClearInputHandler = (event, shouldResetValue) => {

        clearSuggestions();

        if(shouldResetValue) {

            setSearchValue('');

        }
    
    };

    if(loading) {

        return <Skeleton isLoading={loading} />;

    }

    return (
        <Typeahead
            placeholder={dictionary.typeaheadPlaceholder ?? ''}
            onItemClick={onItemClickHandler}
            suggestions={suggestions}
            value={searchValue}
            cancelButtonRef={cancelButtonRef}
            inputRef={inputRef}
            onChange={onChangeHandler}
            onFocus={onFocusHandler}
            onClearInput={onClearInputHandler}
            shouldShowLabels={true}
            baseImageProps={baseImageProps}
        />
    );

};

QuickSearch.propTypes = {
    cancelButtonRef: PropTypes.object,
    typeaheadInputRef: PropTypes.object,
    setFilters: PropTypes.func.isRequired,
    closeFilterModal: PropTypes.func.isRequired,
    onShowSuggestions: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    fieldsData: PropTypes.array,
};

QuickSearch.defaultProps = {
    cancelButtonRef: null,
    typeaheadInputRef: null,
    fieldsData: [],
};

export default QuickSearch;
